import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useRoutesAndScheduleState } from '../../../contexts/routesAndSchedule.context';
import { useTranslation } from 'src/hooks/useTranslationHook';

const SelectedVehicleDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedVehicle, routes, drivers } = useRoutesAndScheduleState();

  if (!selectedVehicle) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="vehicle-details-container"
      >
        <Typography>
          {translateText('administration.routesAndSchedule.noData')}
        </Typography>
      </Box>
    );
  }

  const assignedRoute = routes.find((r) => r.id === selectedVehicle.routeId);
  const assignedDriver = drivers.find((d) => d.id === selectedVehicle.driverId);

  const formatDate = (date: Date | string) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="vehicle-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText(
          'administration.routesAndSchedule.columns.vehicleNumber'
        )}
        : {selectedVehicle.vehicleNumber}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.basicInfo'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-number">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.vehicleNumber'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedVehicle.vehicleNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-type">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.routesAndSchedule.columns.type')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedVehicle.vehicleType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-capacity">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.capacity'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedVehicle.capacity}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-registration">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.registrationNumber'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedVehicle.registrationNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-fuel-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.fuelType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedVehicle.fuelType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="vehicle-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.columns.status'
                )}
              </Typography>
              <Chip
                label={
                  selectedVehicle.isActive
                    ? translateText(
                        'administration.routesAndSchedule.status.active'
                      )
                    : translateText(
                        'administration.routesAndSchedule.status.inactive'
                      )
                }
                color={selectedVehicle.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.assignments'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="vehicle-route">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.route'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedRoute?.routeName ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="vehicle-driver">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.driver'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedDriver?.name ||
                  translateText(
                    'administration.routesAndSchedule.status.unassigned'
                  )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.maintenance'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="vehicle-last-maintenance">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.lastMaintenance'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedVehicle.lastMaintenanceDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="vehicle-next-maintenance">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.nextMaintenance'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedVehicle.nextMaintenanceDate)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('administration.routesAndSchedule.details.legal')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="vehicle-insurance-expiry">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.insuranceExpiry'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedVehicle.insuranceExpiryDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="vehicle-permit-expiry">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.details.permitExpiry'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedVehicle.permitExpiryDate)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SelectedVehicleDetailsView;
